import type { Message } from 'constants/messages';
import {
  APP_OUT_OF_DATE,
  CUSTOM_ASSETS_REVISION,
  EMULATING_DEFAULT_MEMORY_SETTINGS,
  EMULATING_FEATURES,
  EMULATING_PERMISSIONS,
  HAS_TASKS_ADD_ON_TRIAL,
  INCORRECT_TIMEZONE,
  INSTALL_MEMORY_REMINDER,
  NEW_REVISION_AVAILABLE,
  REQUESTED_REVISION_NOT_FOUND,
  SUBSCRIPTION_EXPIRED,
  SUBSCRIPTION_EXPIRED_ADMIN,
  TIC_SERVICE_OFFLINE,
  TRIAL_ENDED,
  TRIAL_ENDED_ADMIN,
  UNSUPPORTED_BROWSER,
} from 'constants/messages';
import { getIsTicServiceOnline } from 'redux/modules/tic/tic.global';
import {
  getCurrentAccountId,
  getIsEmulatingFeatures,
  getIsSubscriptionExpired,
  getIsTrialEnded,
  getTaskAddOnInTrial,
} from 'redux/selectors/accounts';
import {
  getAssetRevisionStatus,
  getDetectedTimezone,
  getIsAppInitialized,
} from 'redux/selectors/global';
import { getShowInstallBanner } from 'redux/selectors/messages/install-banner';
import { getRoutePathName } from 'redux/selectors/routing';
import { getIsEmulatingPermissions, getUser } from 'redux/selectors/user';
import { createSelector } from 'reselect';
import { getBrowserMeta } from 'utils/browser-detection';
import { PREFERRED_HOURS_VIEW } from '~constants/user';
import { getIsViewingDefaults } from '~redux/modules/tic/tic.config_defaults';
import { checkTimezoneMismatch } from '../../utils/user';
import { getIsAnyDiagnosticActive } from './development';
import { getUpgradePermissionsInfo } from './subscriptions';

const readBrowserMeta = () => getBrowserMeta();

export const getEmulationStates = createSelector(
  [getIsEmulatingPermissions, getIsEmulatingFeatures, getIsAnyDiagnosticActive],
  (isEmulatingPermissions, isEmulatingFeatures, isAnyDiagnosticActive) => ({
    isEmulatingPermissions,
    isEmulatingFeatures,
    isAnyDiagnosticActive,
  })
);
export type EmulationStates = ReturnType<typeof getEmulationStates>;

export const getGlobalDetails = createSelector(
  [getCurrentAccountId, getIsAppInitialized, getUser, getDetectedTimezone],
  (accountId, isAppInitialized, user, detectedTimezone) => ({
    accountId,
    isAppInitialized,
    user,
    detectedTimezone,
  })
);

export const getMessages = createSelector(
  [
    getGlobalDetails,
    getIsTrialEnded,
    getIsSubscriptionExpired,
    readBrowserMeta,
    getShowInstallBanner,
    getAssetRevisionStatus,
    getEmulationStates,
    getRoutePathName,
    getTaskAddOnInTrial,
    getUpgradePermissionsInfo,
    getIsTicServiceOnline,
    getIsViewingDefaults,
  ],
  (
    globalDetails,
    isTrialEnded,
    isSubscriptionExpired,
    browserMeta,
    showInstallBanner,
    assetRevisionStatus,
    emulationStates,
    pathname,
    hasTasksAddOnTrial,
    { canUserUpgradeSubscription },
    isTicServiceOnline,
    isViewingDefaults
  ) => {
    const { isAppInitialized, user } = globalDetails;
    const messages: Array<Message> = [];
    const { isSupported, isDesktopApp } = browserMeta;
    const hasDismissedIncorrectTimezone = user.properties?.has_dismissed_incorrect_timezone;
    const isTimezoneMismatch =
      !hasDismissedIncorrectTimezone &&
      checkTimezoneMismatch(user.time_zone, globalDetails.detectedTimezone);
    const isOnboardingRoute = pathname.includes('welcome');
    const isTasksRoute = pathname.includes('tasks');

    if (!isAppInitialized) {
      return [];
    }

    if (isTrialEnded) {
      if (canUserUpgradeSubscription) {
        messages.push(TRIAL_ENDED_ADMIN);
      } else {
        messages.push(TRIAL_ENDED);
      }
    }
    if (hasTasksAddOnTrial != null && isTasksRoute) {
      messages.push(HAS_TASKS_ADD_ON_TRIAL);
    }

    if (isSubscriptionExpired) {
      if (canUserUpgradeSubscription) {
        messages.push(SUBSCRIPTION_EXPIRED_ADMIN);
      } else {
        messages.push(SUBSCRIPTION_EXPIRED);
      }
    }

    if (!isSupported) {
      if (isDesktopApp) {
        messages.push(APP_OUT_OF_DATE);
      } else {
        messages.push(UNSUPPORTED_BROWSER);
      }
    }

    if (isTimezoneMismatch && !isOnboardingRoute) {
      messages.push(INCORRECT_TIMEZONE);
    }

    if (showInstallBanner && !(user.properties[PREFERRED_HOURS_VIEW] === 'timesheet')) {
      messages.push(INSTALL_MEMORY_REMINDER);
    }

    // Don't show custom asset revision banner in dev mode
    if (assetRevisionStatus.isCustomRevisionActive) {
      messages.push(CUSTOM_ASSETS_REVISION);
    }

    if (assetRevisionStatus.assetsNotFound) {
      messages.push(REQUESTED_REVISION_NOT_FOUND);
    }

    if (assetRevisionStatus.newRevisionAvailable) {
      messages.push(NEW_REVISION_AVAILABLE);
    }

    if (emulationStates.isEmulatingPermissions) {
      messages.push(EMULATING_PERMISSIONS);
    }

    if (emulationStates.isEmulatingFeatures) {
      messages.push(EMULATING_FEATURES);
    }

    if (!isTicServiceOnline && pathname.includes('/calendar/')) {
      messages.push(TIC_SERVICE_OFFLINE);
    }

    if (isViewingDefaults) {
      messages.push(EMULATING_DEFAULT_MEMORY_SETTINGS);
    }

    return messages;
  }
);
